/* eslint-disable react-hooks/exhaustive-deps */
import { CheckOutlined, CloseOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Menu, Space, Tag, Typography } from "antd";
import { SvgIcon } from "assets/icons";
import EllipsisText from "components/_storybooks/EllipsisText";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import { CHATWOOT_URL } from "config/platform";
import { DirectContact } from "features/Agent/components/DirectContact";
import { formatBuildQueryString } from "features/Agent/constants";
import {
  useGetSeller,
  useInviteSellers,
  useSellerPermissions,
  useUpdateSellerAccessStatus,
  useUpdateSellerStatus,
} from "hooks/seller";
import { useSupplierPermissions } from "hooks/supplier";
import useDebounce from "hooks/useDebounce";
import { t } from "i18next";
import { compact, isNil, omit } from "lodash";
import ActiveSellerModal from "pages/App/Agent/List/components/ActiveSellerModal";
import {
  FilterContract,
  FilterGroupCustomer,
  FilterInvited,
  FilterLevel,
  FilterSalesman,
  FilterStatus,
  FilterSupplier,
  FilterWarehouse,
} from "pages/App/Agent/List/components/FilterSeller";
import { useEffect, useState } from "react";
import { formatCurrency, getQuery, getTimeStamp, notify } from "utils/helperFuncs";
import AccessStatusCell from "./components/AccessStatusCell";
import "./index.scss";

export const renderInvitedStatus = (isUsed, invitedCount = 0, sellerStatus) => {
  if (isUsed === true && sellerStatus === true) {
    return <SvgIcon.SuccessIcon />;
  } else if (isUsed === false && sellerStatus === true) {
    if (invitedCount > 0)
      return (
        <>
          <div>{`${t("seller.invited")}`}</div>
          <div>{`(${invitedCount} ${t("seller.times")})`}</div>
        </>
      );
    else return t("seller.uninvited");
  } else return;
};

function useAgentTab() {
  const GET_QUERY = getQuery();

  const [params, setParams] = useState({
    filters: {
      statuses: GET_QUERY.statuses || undefined,
      query: GET_QUERY.query || undefined,
      sellerLevelIDs: GET_QUERY.sellerLevelIDs || undefined,
      exportedWarehouseIDs: GET_QUERY.exportedWarehouseIDs || undefined,
      isActive: GET_QUERY.isActive || undefined,
      isSupplier: GET_QUERY.isSupplier || undefined,
      sellerContractStatus: GET_QUERY.sellerContractStatus || undefined,
      sellerGroupIDs: GET_QUERY.sellerGroupIDs || undefined,
      timeRange: GET_QUERY.from
        ? {
            from: GET_QUERY.from,
            to: GET_QUERY.to,
          }
        : undefined,
      periodSelected: GET_QUERY.periodSelected,
    },
    pagination: {
      offset: GET_QUERY.offset || 0,
      limit: GET_QUERY.limit || 10,
    },
  });

  const { loading, data, total, refetch, paginationData } = useGetSeller({
    ...params,
    filters: {
      ...omit(params?.filters, ["periodSelected", "statuses", "inviteStatus"]),
      approvalStatus: "APPROVED",
    },
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const valueDebounce = useDebounce(searchValue?.trim()?.toString());

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const {
    canCreate,
    canDeactivate,
    canView,
    canSellerAccountApproval,
    canUpdateAccessStatus,
    loading: loadingSellerPermissions,
  } = useSellerPermissions();

  const {
    canCreateSupplier,
    canDeactivateSupplier,
    canViewListSupplier,
    loading: loadingSupplierPermissions,
  } = useSupplierPermissions();

  const { handleInviteSellers } = useInviteSellers();
  const { handleUpdateSellerStatus, loading: loadingUpdateSellerStatus } = useUpdateSellerStatus();
  const { handleUpdateAccessStatus, loading: loadingUpdateAccessStatus } = useUpdateSellerAccessStatus();

  // Keep sellerID/nextStatus on close so the modal title does not flip during the close animation
  const [accessChange, setAccessChange] = useState({ visible: false, sellerID: null, nextStatus: null });

  const handleOpenAccessChange = (record, nextStatus) =>
    setAccessChange({ visible: true, sellerID: record?.id, nextStatus });

  const handleCancelAccessChange = () => setAccessChange((prev) => ({ ...prev, visible: false }));

  const handleConfirmAccessChange = async (reason) => {
    try {
      await handleUpdateAccessStatus({ sellerID: accessChange.sellerID, status: accessChange.nextStatus, reason });
      notify.success({ message: t("v2.seller.access_status.update_success") });
      handleCancelAccessChange();
    } catch (error) {
      // Status may have been changed elsewhere (n8n job, seller self-activation): keep the reason, reload rows
      notify.error({ message: t("common.error"), description: error?.message });
      refetch();
    }
  };

  const handleFilterSellerExtra = (values) => {
    const newParams = { ...params, filters: { ...params.filters, ...values } };
    setParams(newParams);
    formatBuildQueryString(newParams);
  };

  function handleFilterSellerByTimeRange(value) {
    const newParams = {
      ...params,
      filters: {
        ...params.filters,
        timeRange: { from: getTimeStamp(value)?.from, to: getTimeStamp(value)?.to },
        periodSelected: value,
      },
      pagination: {
        offset: 0,
        limit: 10,
      },
    };
    setParams(newParams);
    formatBuildQueryString(newParams);
  }

  const handleTableChange = (pagination) => {
    const newParams = {
      ...params,
      pagination: {
        offset: (pagination?.current - 1) * 10,
        limit: 10,
      },
    };
    setParams(newParams);
    formatBuildQueryString(newParams);
  };

  const countSellerInvited = () => {
    let countUninvited = 0;
    let isAllInvited = false;
    selectedRow.forEach((seller) => {
      if (seller.isActive === true && !seller.isLoggedIn) {
        countUninvited++;
      }
    });
    if (countUninvited === selectedRow.length) {
      isAllInvited = true;
    }
    return {
      countUninvited,
      isAllInvited,
    };
  };

  const handleInvitingSellers = async () => {
    if (countSellerInvited().countUninvited !== 0) {
      const activeRows = selectedRow.filter((item) => item.isActive === true) || [];
      const activeKeys = activeRows.map((item) => item.id) || [];

      await handleInviteSellers({ sellerIDs: activeKeys })
        .then((res) => {
          if (res.data.seller.inviteSeller)
            notify.success({
              message: t("seller.successSendInvitation"),
            });
          else
            notify.error({
              message: t("common.error"),
            });
        })
        .catch((error) => {
          notify.error({
            message: t("common.error"),
            description: error.message,
          });
        })
        .finally(() => {
          setSelectedRow([]);
          setSelectedRowKeys([]);
          refetch();
        });
    } else {
      notify.warning({
        message: t("seller.warningEmptyInvitation"),
      });
    }
  };

  const handleCloseTag = (key) => {
    const newParams = { ...params, filters: omit(params.filters, key) };
    setParams(newParams);
    formatBuildQueryString(newParams);
  };

  const handleOpenLinkContactSeller = (record) => {
    const hasActive = record?.users?.some((item) => item.status === "ACTIVE");
    if (hasActive && record?.chatwootContact?.url) {
      window.open(CHATWOOT_URL + "/" + record?.chatwootContact?.url, "_blank");
    } else {
      notify.warning({
        message: t("v2.seller.add_contact_button.message_warning_seller_not_user"),
      });
    }
  };

  const columns = [
    {
      title: t("seller.sellerCode"),
      width: 250,
      dataIndex: "code",
      fixed: "left",
      render: (text, record, index) => (
        <EllipsisText isLink={true} title={text} link={`seller/detail/${record.id}`} strong />
      ),
    },
    {
      title: t("seller.sellerName"),
      width: 250,
      fixed: "left",
      dataIndex: "fullName",
      render: (text, record, index) => {
        return <CustomTextHiddenLine line={2} text={record?.fullName} />;
      },
    },
    {
      title: t("common.address"),
      width: 250,
      render: (text, record, index) => {
        return (
          <CustomTextHiddenLine
            line={2}
            text={compact([record?.address, record?.ward?.name, record?.district?.name, record?.city?.name]).join(", ")}
          />
        );
      },
    },
    {
      title: t("common.level"),
      align: "center",
      width: 100,
      dataIndex: ["sellerLevel", "level"],
      filterDropdown: <FilterLevel onFilter={handleFilterSellerExtra} />,
    },
    {
      title: t("seller.sellerDebtLimit"),
      dataIndex: "debtLimit",
      align: "right",
      width: 200,
      render: (text) => formatCurrency(text),
    },
    {
      title: t("seller.sellerDebtAge"),
      dataIndex: "debtAge",
      align: "center",
      width: 100,
      render: (_, record) => record?.debtAge || "0",
    },
    {
      title: t("seller.warehouse"),
      width: 320,
      render: (text, record, index) => (
        <Space wrap={true}>
          {record.warehouses?.map(({ name }, index) => (
            <Tag color="blue" key={index}>
              {name}
            </Tag>
          ))}
        </Space>
      ),
      filterDropdown: <FilterWarehouse onFilter={handleFilterSellerExtra} />,
    },
    ...(canDeactivate || canDeactivateSupplier
      ? [
          {
            title: t("common.status"),
            align: "center",
            width: 100,
            dataIndex: "isActive",
            render: (text, record, index) => (
              <Space style={{ width: "100%", justifyContent: "center" }}>
                <ActiveSellerModal
                  isActive={record.isActive}
                  loading={loadingUpdateSellerStatus}
                  onOke={(value) => {
                    handleUpdateSellerStatus({
                      id: record.id,
                      isActive: !record.isActive,
                      reason: !record.isActive ? undefined : value,
                    });
                  }}
                />
              </Space>
            ),
            filterDropdown: <FilterStatus onFilter={handleFilterSellerExtra} />,
          },
        ]
      : []),
    {
      title: t("seller.groupCustomer"),
      dataIndex: ["sellerGroup", "name"],
      filterDropdown: <FilterGroupCustomer onFilter={handleFilterSellerExtra} />,
    },
    // {
    //   title: t("seller.salesMan"),
    //   width: 180,
    //   render: (text, record, index) => {
    //     return record?.sellerGroup?.manager?.fullname ? (
    //       <CustomTextHiddenLine line={2} text={record?.sellerGroup?.manager?.fullname} />
    //     ) : (
    //       <Typography.Text style={{ color: "#9CADC4" }}>--</Typography.Text>
    //     );
    //   },
    //   filterDropdown: <FilterSalesman onFilter={handleFilterSellerExtra} />,
    // },
    {
      title: <span>HỢP ĐỒNG NGUYÊN TẮC</span>,
      dataIndex: "sellerContractStatus",
      width: 200,
      align: "center",
      filterDropdown: <FilterContract onFilter={handleFilterSellerExtra} />,
      render: (_, record) => {
        const statusMap = {
          NONE: { text: "Chưa có", color: "#2246DC", bgColor: "rgba(34, 70, 220, 0.15)" },
          REQUIRED: { text: "Cần ký hợp đồng", color: "#D48806", bgColor: "rgba(255, 197, 61, 0.2)" },
          ACTIVE: { text: "Còn hiệu lực", color: "#00AB78", bgColor: "rgba(0, 171, 120, 0.15)" },
          EXPIRED: { text: "Hết hiệu lực", color: "#CF1322", bgColor: "rgba(255, 77, 79, 0.15)" },
          DISABLED: { text: "Bị vô hiệu", color: "#1F1F1F", bgColor: "rgba(31, 31, 31, 0.15)" },
        };
        const status = record?.sellerContractStatus || "NONE";
        const statusInfo = statusMap[status] || statusMap.NONE;

        return (
          <Space wrap={true}>
            <Tag
              style={{
                color: statusInfo.color,
                backgroundColor: statusInfo.bgColor,
                border: `1px solid ${statusInfo.color}`,
                borderRadius: "999px",
                padding: "2px 10px",
                fontWeight: 500,
              }}
            >
              {statusInfo.text}
            </Tag>
          </Space>
        );
      },
    },
    // {
    //   title: "KẾ TOÁN PHỤ TRÁCH",
    //   width: 180,
    //   render: (text, record, index) => {
    //     return record?.sellerGroup?.manager?.fullname ? (
    //       <CustomTextHiddenLine line={2} text={record?.sellerGroup?.manager?.fullname} />
    //     ) : (
    //       <Typography.Text style={{ color: "#9CADC4" }}>--</Typography.Text>
    //     );
    //   },
    // },
    {
      title: t("seller.salesMan"),
      width: 180,
      render: (text, record, index) => {
        return record?.salesMan?.fullname ? (
          <CustomTextHiddenLine line={2} text={record?.salesMan?.fullname} />
        ) : (
          <Typography.Text style={{ color: "#9CADC4" }}>--</Typography.Text>
        );
      },
      filterDropdown: <FilterSalesman onFilter={handleFilterSellerExtra} />,
    },
    {
      title: t("seller.isSupplier"),
      align: "center",
      width: 100,
      render: (text, record, index) =>
        record?.isSupplier && record?.isSeller ? (
          <CheckOutlined style={{ color: "#2246DC", fontSize: "24px" }} />
        ) : (
          <CloseOutlined style={{ fontSize: "24px" }} />
        ),
      filterDropdown: <FilterSupplier onFilter={handleFilterSellerExtra} />,
    },
    {
      title: t("seller.usedApp"),
      align: "center",
      width: 100,
      render: (text, record, index) => renderInvitedStatus(record?.isLoggedIn, record?.countInvite, record?.isActive),
      filterDropdown: <FilterInvited onFilter={handleFilterSellerExtra} />,
    },
    {
      title: t("v2.seller.access_status.action_group_title"),
      align: "center",
      fixed: "right",
      children: [
        {
          title: t("v2.seller.access_status.column_title"),
          key: "accessStatus",
          align: "center",
          width: 150,
          fixed: "right",
          render: (text, record, index) => (
            <AccessStatusCell
              status={record?.accessStatus}
              canUpdate={canUpdateAccessStatus}
              onChange={(nextStatus) => handleOpenAccessChange(record, nextStatus)}
            />
          ),
        },
        {
          title: t("v2.seller.access_status.contact_column_title"),
          key: "contact",
          align: "center",
          width: 170,
          fixed: "right",
          render: (text, record, index) => {
            return (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item>
                      <DirectContact listUsers={record?.users} />
                    </Menu.Item>
                    <Menu.Item onClick={() => handleOpenLinkContactSeller(record)}>
                      <Typography.Text>Nhắn tin nhóm</Typography.Text>
                    </Menu.Item>
                  </Menu>
                }
                className="dropdown-action-contact"
                disabled={!record?.isActive}
              >
                <Button>
                  Liên hệ đại lý <DownOutlined />
                </Button>
              </Dropdown>
            );
          },
        },
      ],
    },
  ];

  useEffect(() => {
    if (!isNil(valueDebounce)) {
      const newParams = {
        ...params,
        filters: { ...params?.filters, query: valueDebounce.trim() || undefined },
        pagination: {
          offset: 0,
          limit: 10,
        },
      };
      setParams(newParams);
      formatBuildQueryString(newParams);
    }
  }, [valueDebounce]);

  useEffect(() => {
    let newParams = {
      ...params,
      filters: {
        ...params.filters,
        isSupplier: GET_QUERY.isSupplier,
        isActive: GET_QUERY.isActive,
      },
    };
    setParams(newParams);
    formatBuildQueryString(newParams);
  }, []);

  return {
    loading,
    data,
    total,
    canCreate,
    canView,
    canSellerAccountApproval,
    loadingSellerPermissions,
    canCreateSupplier,
    canViewListSupplier,
    loadingSupplierPermissions,
    columns,
    params,
    searchValue,
    selectedRowKeys,
    paginationData,
    countSellerInvited,
    handleTableChange,
    handleFilterSellerByTimeRange,
    handleInvitingSellers,
    handleCloseTag,
    handleSearch,
    setSelectedRowKeys,
    setSelectedRow,
    accessChange,
    loadingUpdateAccessStatus,
    handleCancelAccessChange,
    handleConfirmAccessChange,
  };
}

export default useAgentTab;
